/**
 * Media Service — Firebase Storage upload / delete for day audio & images.
 *
 * Storage paths:
 *   Audio : {audience}/day-{dayNumber}/audio/{timestamp}_{filename}
 *   Image : {audience}/day-{dayNumber}/images/{timestamp}_{filename}
 *
 * After a successful upload, the caller should immediately merge the
 * url + storagePath into the Firestore day document via contentService.saveDayContent.
 */

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTask,
} from 'firebase/storage';
import { storage } from '@/config/firebase';
import {
  validateAudioFile,
  validateImageFile,
  generateStorageFilename,
  buildStoragePath,
} from '@/utils/fileUtils';
import type { Audience } from '@/types/auth.types';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface UploadResult {
  url:         string;
  storagePath: string;
}

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes:       number;
  percentage:       number;
}

// ─── Internal helper ───────────────────────────────────────────────────────────

/**
 * Uploads a file and calls progress / complete / error callbacks.
 * Returns a cancel function.
 */
function startUpload(
  storagePath: string,
  file: File,
  onProgress: (p: UploadProgress) => void,
  onComplete: (result: UploadResult) => void,
  onError:    (err: Error) => void,
): () => void {
  const storageRef = ref(storage, storagePath);
  const task: UploadTask = uploadBytesResumable(storageRef, file);

  task.on(
    'state_changed',
    snapshot => {
      const pct = snapshot.totalBytes > 0
        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        : 0;
      onProgress({
        bytesTransferred: snapshot.bytesTransferred,
        totalBytes:       snapshot.totalBytes,
        percentage:       pct,
      });
    },
    err => onError(err instanceof Error ? err : new Error(String(err))),
    async () => {
      try {
        const url = await getDownloadURL(task.snapshot.ref);
        onComplete({ url, storagePath });
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  );

  return () => task.cancel();
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Validates and uploads an audio file to Firebase Storage.
 * Returns a cancel function.
 */
export function uploadAudio(
  audience: Audience,
  dayNumber: number,
  file: File,
  onProgress: (p: UploadProgress) => void,
  onComplete: (result: UploadResult) => void,
  onError:    (err: Error) => void,
): () => void {
  const validation = validateAudioFile(file);
  if (!validation.valid) {
    onError(new Error(validation.error ?? 'Invalid audio file'));
    return () => {};
  }

  const filename    = generateStorageFilename(file.name);
  const storagePath = buildStoragePath(audience, dayNumber, 'audio', filename);
  return startUpload(storagePath, file, onProgress, onComplete, onError);
}

/**
 * Validates and uploads an image file to Firebase Storage.
 * Returns a cancel function.
 */
export function uploadImage(
  audience: Audience,
  dayNumber: number,
  file: File,
  onProgress: (p: UploadProgress) => void,
  onComplete: (result: UploadResult) => void,
  onError:    (err: Error) => void,
): () => void {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    onError(new Error(validation.error ?? 'Invalid image file'));
    return () => {};
  }

  const filename    = generateStorageFilename(file.name);
  const storagePath = buildStoragePath(audience, dayNumber, 'images', filename);
  return startUpload(storagePath, file, onProgress, onComplete, onError);
}

/**
 * Deletes a file from Firebase Storage by its storage path.
 * Silently ignores "not found" errors (file may already be deleted).
 */
export async function deleteMedia(storagePath: string): Promise<void> {
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    if (code === 'storage/object-not-found') return; // already gone — fine
    throw err;
  }
}
